import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SignupData } from '../types';

const Signup: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupData>();

  const onSubmit = async (data: SignupData) => {
    try {
      setError('');
      setLoading(true);
      await signup(data);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { borderWidth: '1.5px', borderColor: '#BFDBFE', borderRadius: '12px' } as const;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden gradient-login py-10">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="flex justify-center mb-4">
          <img src="/images/icare-logo.jpg" alt="icare.ai" className="w-[90px] h-[90px] rounded-2xl shadow-blue-lg" />
        </div>

        <h2 className="text-2xl font-bold text-center text-primary-800 mb-1" style={{ letterSpacing: '-0.02em' }}>
          Register your care home
        </h2>
        <p className="text-center text-primary-700 text-base font-medium opacity-80 mb-7">
          Create your organisation and admin account
        </p>

        <div className="bg-white rounded-3xl p-6 border shadow-blue-lg" style={{ borderColor: 'rgba(191, 219, 254, 0.5)' }}>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="rounded-md bg-danger-bg border border-danger-light p-3">
                <div className="text-sm text-danger-dark font-medium">{error}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Care home name</label>
              <input
                {...register('organizationName', { required: 'Care home name is required' })}
                type="text"
                className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                style={inputStyle}
                placeholder="e.g. Riverside Care Home"
              />
              {errors.organizationName && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.organizationName.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Your name</label>
              <input
                {...register('name', { required: 'Your name is required' })}
                type="text"
                className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                style={inputStyle}
                placeholder="Full name"
              />
              {errors.name && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Email address</label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                })}
                type="email"
                className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                style={inputStyle}
                placeholder="you@careplanpro.com"
              />
              {errors.email && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Password</label>
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                })}
                type="password"
                className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                style={inputStyle}
                placeholder="Create a password"
              />
              {errors.password && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-[52px] gradient-button text-white font-semibold text-md tracking-wide disabled:opacity-50 transition-all duration-200"
              style={{ borderRadius: '14px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.5)', letterSpacing: '0.5px' }}
            >
              {loading ? 'Creating account...' : 'Create care home'}
            </button>

            <div className="flex justify-center pt-1">
              <span className="text-base text-gray-600">Already have an account? </span>
              <button type="button" onClick={() => navigate('/login')} className="text-base text-primary-600 font-semibold ml-1">
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
